import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiBaseUrl, apiPostBlob, ApiError } from "../../lib/api";
import type { ID } from "../id";
import { bumpPhotoVersion } from "../photoVersion";

// personPhotoUrl builds the authenticated URL for a person's photo. The
// endpoint requires the session cookie, so this is used directly as an
// <img src> (the browser sends the cookie) — never a public URL.
//
// `"thumb"` is the small copy the server makes from the upload (shorter
// side 160px). Every surface draws a photo at 72px or less, so every
// surface asks for it; the bare URL is the original, byte for byte, and
// decoding a camera original for a 34px chip is what made the roster
// flash while scrolling.
//
// `version` is the cache-buster a caller bumps after an upload: the URL is
// otherwise stable, and the browser would keep showing the replaced photo.
export function personPhotoUrl(
  personId: ID,
  { size, version }: { size?: "thumb"; version?: number } = {},
): string {
  const params = new URLSearchParams();
  if (size) params.set("size", size);
  if (version) params.set("v", String(version));
  const query = params.toString();
  return `${apiBaseUrl}/persons/${personId}/photo${query ? `?${query}` : ""}`;
}

// A photo change moves the team's "X/Y fotek" rollup and can flip its
// missing_photo state, so invalidate the whole teams cache — the admin
// list keys on ["teams", filters] (a sibling, not a descendant of
// ["teams", teamId]) — plus the current user's teams. Mirrors
// useSetTeamPayment. The members registry keys on ["persons", ...] and
// shows photo_uploaded, so that cache goes too.
function invalidatePhoto(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["teams"] });
  qc.invalidateQueries({ queryKey: ["me", "teams"] });
  qc.invalidateQueries({ queryKey: ["persons"] });
}

// teamId null routes the upload through the global POST /persons/:id/photo
// (admin / members_admin only) — the members registry's path. Explicit null
// rather than optional, so a caller who merely forgot the team scope fails
// to compile instead of silently hitting an endpoint that 403s them.
export function useUploadPhoto(teamId: ID | null, personId: ID) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, Blob>({
    mutationFn: (blob) =>
      apiPostBlob(
        teamId === null
          ? `/persons/${personId}/photo`
          : `/teams/${teamId}/persons/${personId}/photo`,
        blob,
      ),
    // PhotoUploadField renders the error inline; the global toast would say
    // the same thing twice.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      // The new bytes sit behind the same URL; the version makes every
      // reader of usePhotoVersion ask for them instead of the cached ones.
      bumpPhotoVersion(personId);
      invalidatePhoto(qc);
    },
  });
}

// There is deliberately no useRemovePhoto: a photo is replaced, never
// removed (see PhotoUploadField). DELETE /teams/:id/persons/:personId/photo
// still exists on the backend — the bytes are PII, often of a minor, so a
// purge must stay possible — but nothing in the UI offers it.
